import React, { useEffect, useMemo } from 'react';
import { Col } from 'antd';
import moment, { Moment } from 'moment';
import { LoadingSpin } from 'common/components/LoadingSpin';
import CustomDatePicker from 'common/components/DateTime/CustomDatePicker';
import { DATE_FORMAT } from 'common/const/common.const';
import { useQueryFilter } from 'common/hooks/useQueryFilter';
import { rangeConfig } from 'common/helpers/date.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getStatisticGlobalModel, getStatisticModel } from 'app/store/reducers/statistic.reducer';
import { OrderCard } from 'entities/Statistic/components/Card/OrderCard';
import { StatsCard } from 'entities/Statistic/components/Card/StatsCard';
import { StoreSelector } from 'entities/Store/components/Selector/StoreSelector';
import { IStoreModel, selectorStoreAllKeyId } from 'entities/Store/Store.models';
import { EStatisticsTab } from 'entities/Statistic/Statistic.models';

interface IFilter {
  tab?: string;
  store?: Partial<IStoreModel>;
  dateFrom?: string;
  dateTo?: string;
}

export const StatisticsStoresTab: React.FC = () => {
  const dispach = useAppDispatch();
  const { statisticModel, statisticGlobalModel } = useAppSelector((state) => state.statistic);
  const defaultFilter: IFilter = useMemo(
    () => ({
      tab: EStatisticsTab.Stores,
      store: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    }),
    []
  );

  const { filter, updateFilter } = useQueryFilter(defaultFilter);

  const globalData = statisticGlobalModel;
  const statisticData = filter.store?.id ? { ...statisticModel } : { ...globalData };
  const defaultDateValue: [Moment, Moment] | undefined =
    filter.dateFrom && filter.dateTo ? [moment(filter.dateFrom), moment(filter.dateTo)] : undefined;

  const onChangeSelector = (item?: IStoreModel) => {
    const isAll = item?.id === selectorStoreAllKeyId;
    const store = item && !isAll ? { id: item?.id, storeName: item?.storeName } : undefined;

    updateFilter({ store });
  };

  const onChangeDate = (value: any[] | null) => {
    if (value?.length) {
      const dateFrom = moment(value[0]).format();
      const dateTo = moment(value[1]).endOf('day').format();

      updateFilter({ dateFrom, dateTo });
    } else {
      updateFilter({ dateFrom: undefined, dateTo: undefined });
    }
  };

  useEffect(() => {
    const { store, dateFrom, dateTo } = filter;

    store?.id
      ? dispach(getStatisticModel({ id: store.id, dateFrom, dateTo }))
      : dispach(getStatisticGlobalModel({ dateFrom, dateTo }));
  }, [filter]);

  return (
    <>
      <Col span={24} className="mt-5 mb-5">
        <div className="fs-xxs font-family__default mb-3">Store</div>
        <StoreSelector
          onChange={onChangeSelector}
          value={filter.store}
          defaultOpt={{ id: selectorStoreAllKeyId }}
          placeholder="Enter store name"
          allowClear={false}
        />
      </Col>
      <Col span={24}>
        <div className="fs-xxs font-family__default mb-3">Time range</div>
        <CustomDatePicker.RangePicker
          className="width-full franchisee__date-picker"
          defaultValue={defaultDateValue}
          presets={rangeConfig}
          getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
          format={DATE_FORMAT}
          onChange={onChangeDate}
          showTime={false}
        />
      </Col>
      {statisticData ? (
        <>
          <StatsCard item={statisticData} />
          <OrderCard item={statisticData} />
        </>
      ) : (
        <LoadingSpin />
      )}
    </>
  );
};
